var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box, CircularProgress, } from '@mui/material';
import isEqual from 'lodash-es/isEqual';
import React, { Children, useCallback, useEffect, useRef, useState, } from 'react';
import { getFilteredData } from './utils';
var ChildType;
(function (ChildType) {
    ChildType["ApDataGridColumn"] = "ApDataGridColumn";
    ChildType["ApDataGridFooter"] = "ApDataGridFooter";
    ChildType["ApDataGridRowActions"] = "ApDataGridRowActions";
    ChildType["ApDataGridRowButton"] = "ApDataGridRowButton";
    ChildType["ApDataGridHeader"] = "ApDataGridHeader";
    ChildType["ApDataGridHeaderButton"] = "ApDataGridHeaderButton";
    ChildType["ApDataGridColumnSearchFilter"] = "ApDataGridColumnSearchFilter";
    ChildType["ApDataGridColumnDropdownFilter"] = "ApDataGridColumnDropdownFilter";
})(ChildType || (ChildType = {}));
const SLOT_SEPARATOR = '__slot__';
var SlotType;
(function (SlotType) {
    SlotType["Cell"] = "cell";
    SlotType["RowButton"] = "rowButton";
    SlotType["Header"] = "header";
    SlotType["Expanded"] = "expanded";
    SlotType["CardView"] = "cardView";
    SlotType["NoContent"] = "noContent";
})(SlotType || (SlotType = {}));
export const ApDataGridWrapper = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const apGridRef = useRef(null);
    const sortSubscriptionRef = useRef(null);
    const filterSubscriptionRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [displayedData, setDisplayedData] = useState();
    const [slots, setSlots] = useState();
    const [slotNodes, setSlotNodes] = useState();
    const [gridReady, setGridReady] = useState(false);
    const [searchTerm, setSearchTerm] = useState((_b = (_a = props.initialFilters) === null || _a === void 0 ? void 0 : _a.searchTerm) !== null && _b !== void 0 ? _b : '');
    const [sortOpts, setSortOpts] = useState((_d = (_c = props.initialFilters) === null || _c === void 0 ? void 0 : _c.sort) !== null && _d !== void 0 ? _d : []);
    const [filterOpts, setFilterOpts] = useState((_f = (_e = props.initialFilters) === null || _e === void 0 ? void 0 : _e.filter) !== null && _f !== void 0 ? _f : []);
    const [sortFunction, setSortFunction] = useState();
    const [footerProps, setFooterProps] = useState();
    const [headerProps, setHeaderProps] = useState();
    const [columnProps, setColumnProps] = useState();
    const [rowButtonProps, setRowButtonProps] = useState();
    const { children: apGridChildren, expandRenderer, noContentRenderer, unpaginated, data, onChange, onGridApi, theme, language } = props, apGridOtherProps = __rest(props, ["children", "expandRenderer", "noContentRenderer", "unpaginated", "data", "onChange", "onGridApi", "theme", "language"]);
    const calculateData = useCallback(() => {
        const { pageSize, pageIndex, } = footerProps !== null && footerProps !== void 0 ? footerProps : {};
        if (pageIndex !== undefined && pageSize) {
            let finalData = [...data];
            if (sortFunction) {
                finalData = finalData.sort(sortFunction);
            }
            if ((searchTerm || filterOpts.length) && columnProps) {
                finalData = getFilteredData({
                    data: finalData,
                    search: searchTerm,
                    filters: filterOpts,
                    columns: columnProps,
                });
            }
            const newData = finalData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
            if (!isEqual(newData, displayedData)) {
                setDisplayedData(newData);
                if (footerProps && footerProps.length !== finalData.length) {
                    setFooterProps(prev => (Object.assign(Object.assign({}, prev), { length: finalData.length })));
                }
            }
            setLoading(false);
        }
    }, [footerProps, data, sortFunction, searchTerm, columnProps, filterOpts, displayedData]);
    useEffect(() => () => {
        var _a, _b;
        (_a = sortSubscriptionRef.current) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        (_b = filterSubscriptionRef.current) === null || _b === void 0 ? void 0 : _b.unsubscribe();
    }, []);
    useEffect(() => {
        if (unpaginated) {
            calculateData();
        }
        else if (data && !isEqual(data, displayedData)) {
            setDisplayedData(data);
            setLoading(false);
        }
    }, [calculateData, unpaginated, footerProps, data]);
    useEffect(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange({
            filter: filterOpts,
            sort: sortOpts,
            pageIndex: footerProps === null || footerProps === void 0 ? void 0 : footerProps.pageIndex,
            pageSize: footerProps === null || footerProps === void 0 ? void 0 : footerProps.pageSize,
            searchTerm: searchTerm !== null && searchTerm !== void 0 ? searchTerm : '',
        });
    }, [
        filterOpts,
        sortOpts,
        searchTerm,
        footerProps === null || footerProps === void 0 ? void 0 : footerProps.pageIndex,
        footerProps === null || footerProps === void 0 ? void 0 : footerProps.pageSize,
        onChange,
    ]);
    useEffect(() => {
        if (!props.children) {
            return;
        }
        const childProps = {};
        Children.forEach(props.children, (child) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            if (child && !Array.isArray(child)) {
                const _r = child.props, { children, render } = _r, otherProps = __rest(_r, ["children", "render"]);
                const sort = (_c = (_b = (_a = sortOpts === null || sortOpts === void 0 ? void 0 : sortOpts.find(sortOpt => sortOpt.field === otherProps.property)) === null || _a === void 0 ? void 0 : _a.direction) !== null && _b !== void 0 ? _b : otherProps.sort) !== null && _c !== void 0 ? _c : (_e = (_d = apGridRef.current) === null || _d === void 0 ? void 0 : _d.configuration) === null || _e === void 0 ? void 0 : _e.sort;
                switch (child.type.displayName) {
                    case ChildType.ApDataGridColumn:
                        if (!childProps[ChildType.ApDataGridColumn]) {
                            childProps[ChildType.ApDataGridColumn] = [];
                        }
                        (_f = childProps[ChildType.ApDataGridColumn]) === null || _f === void 0 ? void 0 : _f.push(Object.assign(Object.assign(Object.assign(Object.assign({}, otherProps), Children
                            .toArray(children)
                            .reduce((acc, grandChild) => {
                            if (grandChild.type.displayName === ChildType.ApDataGridColumnSearchFilter) {
                                acc.searchFilterOpts = Object.assign({}, grandChild.props);
                            }
                            else if (grandChild.type.displayName === ChildType.ApDataGridColumnDropdownFilter) {
                                acc.dropdownFilterOpts = Object.assign({}, grandChild.props);
                            }
                            return acc;
                        }, {})), render ? {
                            render,
                            isCustom: true,
                        } : {}), sort ? { sort } : {}));
                        break;
                    case ChildType.ApDataGridFooter:
                        childProps[ChildType.ApDataGridFooter] = Object.assign(Object.assign({}, otherProps), { pageSize: (_k = (_h = (_g = otherProps.pageSize) !== null && _g !== void 0 ? _g : footerProps === null || footerProps === void 0 ? void 0 : footerProps.pageSize) !== null && _h !== void 0 ? _h : (_j = props.initialFilters) === null || _j === void 0 ? void 0 : _j.pageSize) !== null && _k !== void 0 ? _k : 10, pageIndex: (_p = (_m = (_l = otherProps.pageIndex) !== null && _l !== void 0 ? _l : footerProps === null || footerProps === void 0 ? void 0 : footerProps.pageIndex) !== null && _m !== void 0 ? _m : (_o = props.initialFilters) === null || _o === void 0 ? void 0 : _o.pageIndex) !== null && _p !== void 0 ? _p : 0 });
                        break;
                    case ChildType.ApDataGridRowActions:
                        childProps[ChildType.ApDataGridRowActions] = Children.map(children, (grandChild) => {
                            if (!grandChild) {
                                return {};
                            }
                            if (grandChild.type.displayName !== ChildType.ApDataGridRowButton) {
                                throw new Error(`Invalid child of ${ChildType.ApDataGridRowActions} type: ${grandChild.type.displayName}`);
                            }
                            return Object.assign(Object.assign({}, grandChild.props), grandChild.props.render ? {
                                render: grandChild.props.render,
                                isCustom: true,
                            } : {});
                        });
                        break;
                    case ChildType.ApDataGridHeader:
                        childProps[ChildType.ApDataGridHeader] = Object.assign(Object.assign({}, otherProps), { gridHeaderButtons: Children.map(children, (grandChild) => {
                                if (grandChild.type.displayName !== ChildType.ApDataGridHeaderButton) {
                                    throw new Error(`Invalid child of ${ChildType.ApDataGridHeader} type: ${grandChild.type.displayName}`);
                                }
                                return Object.assign(Object.assign({}, grandChild.props), grandChild.props.render ? {
                                    render: grandChild.props.render,
                                    isCustom: true,
                                } : {});
                            }) });
                        (_q = childProps[ChildType.ApDataGridHeader]) === null || _q === void 0 ? true : delete _q.children;
                        break;
                    case undefined:
                    case false:
                    case null:
                        break;
                    default:
                        throw new Error(`Invalid child type: ${child.type.displayName}`);
                }
            }
        });
        setFooterProps(prev => (Object.assign(Object.assign({}, prev), childProps[ChildType.ApDataGridFooter])));
        setHeaderProps(prev => (Object.assign(Object.assign({}, prev), childProps[ChildType.ApDataGridHeader])));
        setColumnProps(childProps[ChildType.ApDataGridColumn]);
        setRowButtonProps(childProps[ChildType.ApDataGridRowActions]);
        setGridReady(true);
    }, [props.children]);
    useEffect(() => {
        if (!unpaginated || !(sortOpts === null || sortOpts === void 0 ? void 0 : sortOpts.length)) {
            return;
        }
        setSortFunction(() => (prev, cur) => {
            for (const sortOption of sortOpts) {
                if (!(sortOption === null || sortOption === void 0 ? void 0 : sortOption.direction) || prev[sortOption.field] === cur[sortOption.field]) {
                    continue;
                }
                if (sortOption.direction === 'asc') {
                    return prev[sortOption.field] > cur[sortOption.field] ? 1 : -1;
                }
                return prev[sortOption.field] < cur[sortOption.field] ? 1 : -1;
            }
            return 0;
        });
    }, [unpaginated, sortOpts]);
    const gridApi = useCallback((api) => {
        if (sortSubscriptionRef.current) {
            sortSubscriptionRef.current.unsubscribe();
        }
        if (filterSubscriptionRef.current) {
            filterSubscriptionRef.current.unsubscribe();
        }
        sortSubscriptionRef.current = api.sortManager.sort$.subscribe((sort) => {
            if (Object.keys(sort).length === 0) {
                return;
            }
            setSortOpts(prev => {
                if (!prev) {
                    return [sort];
                }
                const hasSort = prev.find(s => s.field === sort.field);
                if (hasSort) {
                    const index = prev.findIndex(s => s.field === sort.field);
                    prev.splice(index, 1);
                }
                return [sort, ...prev];
            });
        });
        let skippedFirst = false;
        filterSubscriptionRef.current = api.filterManager.filter$.subscribe((filter) => {
            if (!skippedFirst) {
                skippedFirst = true;
                return;
            }
            setFilterOpts(filter);
        });
        onGridApi === null || onGridApi === void 0 ? void 0 : onGridApi(api);
    }, [onGridApi]);
    useEffect(() => {
        if (apGridRef === null || apGridRef === void 0 ? void 0 : apGridRef.current) {
            const handleSlotsChanged = (event) => {
                setSlots((prev) => Array.from(new Set([...prev !== null && prev !== void 0 ? prev : [], ...event.detail])));
            };
            apGridRef.current.addEventListener('slotsChanged', handleSlotsChanged);
            return () => {
                var _a;
                (_a = apGridRef === null || apGridRef === void 0 ? void 0 : apGridRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('slotsChanged', handleSlotsChanged);
            };
        }
    }, [apGridRef]);
    useEffect(() => {
        var _a;
        if (apGridRef.current && gridReady) {
            const _b = (headerProps !== null && headerProps !== void 0 ? headerProps : {}), { render: _ } = _b, restHeaderProps = __rest(_b, ["render"]);
            apGridRef.current.configuration = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, apGridOtherProps), { data: displayedData !== null && displayedData !== void 0 ? displayedData : [], columns: (_a = columnProps === null || columnProps === void 0 ? void 0 : columnProps.map((column) => {
                    var _a, _b;
                    const { render } = column, finalColumn = __rest(column, ["render"]);
                    if (filterOpts) {
                        const filter = filterOpts.find(f => f.property === finalColumn.property);
                        if (!filter) {
                            return finalColumn;
                        }
                        if (finalColumn.searchFilterOpts) {
                            finalColumn.searchFilterOpts.value = (_a = filter === null || filter === void 0 ? void 0 : filter.meta) !== null && _a !== void 0 ? _a : undefined;
                        }
                        if (finalColumn.dropdownFilterOpts) {
                            const matching = (_b = finalColumn.dropdownFilterOpts.items) === null || _b === void 0 ? void 0 : _b.find(item => item.value === filter.value);
                            finalColumn.dropdownFilterOpts.value = matching;
                        }
                    }
                    return finalColumn;
                })) !== null && _a !== void 0 ? _a : [] }), footerProps ? {
                footerOptions: Object.assign(Object.assign({}, footerProps), { pageChange: (event) => {
                        var _a;
                        setFooterProps(prev => (Object.assign(Object.assign({}, prev), { pageIndex: event.pageSize === (prev === null || prev === void 0 ? void 0 : prev.pageSize) ? event.pageIndex : 0, pageSize: event.pageSize })));
                        (_a = footerProps.pageChange) === null || _a === void 0 ? void 0 : _a.call(footerProps, event);
                    } }),
            } : {}), rowButtonProps ? {
                gridRowButtons: rowButtonProps.map((rowButtonProp) => {
                    const { render } = rowButtonProp, rest = __rest(rowButtonProp, ["render"]);
                    return rest;
                }),
            } : {}), headerProps ? {
                headerOptions: Object.assign(Object.assign({}, restHeaderProps), { searchTerm: (term) => {
                        var _a;
                        (_a = headerProps.searchTerm) === null || _a === void 0 ? void 0 : _a.call(headerProps, term);
                        setSearchTerm(term);
                    } }),
            } : {}), expandRenderer ? { expandRenderer } : {}), { hasCustomNoContentRenderer: !!noContentRenderer, onGridApi: gridApi });
        }
    }, [
        filterOpts,
        displayedData,
        columnProps,
        footerProps,
        rowButtonProps,
        headerProps,
        expandRenderer,
        apGridOtherProps,
        apGridRef,
        unpaginated,
        gridReady,
        gridApi,
    ]);
    const displaySlots = useCallback(() => slots === null || slots === void 0 ? void 0 : slots.map(slot => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (typeof slot !== 'string') {
            return;
        }
        const type = slot === null || slot === void 0 ? void 0 : slot.split(SLOT_SEPARATOR)[0];
        if (!type) {
            return;
        }
        if (type === SlotType.Cell) {
            const [, property, rowId] = slot.split(SLOT_SEPARATOR);
            const found = displayedData === null || displayedData === void 0 ? void 0 : displayedData[+rowId];
            return (React.createElement("div", { key: slot, slot: slot }, found && ((_b = (_a = columnProps === null || columnProps === void 0 ? void 0 : columnProps.find(c => c.property === property)) === null || _a === void 0 ? void 0 : _a.render) === null || _b === void 0 ? void 0 : _b.call(_a, found))));
        }
        if (type === SlotType.RowButton) {
            const [, buttonId, rowId] = slot.split(SLOT_SEPARATOR);
            const found = displayedData === null || displayedData === void 0 ? void 0 : displayedData[+rowId];
            return (React.createElement("div", { key: slot, slot: slot }, found && ((_d = (_c = rowButtonProps === null || rowButtonProps === void 0 ? void 0 : rowButtonProps.find(c => c.id === buttonId)) === null || _c === void 0 ? void 0 : _c.render) === null || _d === void 0 ? void 0 : _d.call(_c, displayedData[+rowId], +rowId))));
        }
        if (type === SlotType.Header) {
            const buttonId = slot.split(SLOT_SEPARATOR)[1];
            return (React.createElement("div", { key: slot, slot: slot }, (_g = (_f = (_e = headerProps === null || headerProps === void 0 ? void 0 : headerProps.gridHeaderButtons) === null || _e === void 0 ? void 0 : _e.find(h => h.id === buttonId)) === null || _f === void 0 ? void 0 : _f.render) === null || _g === void 0 ? void 0 : _g.call(_f)));
        }
        if (type === SlotType.Expanded) {
            const entryId = slot.split(SLOT_SEPARATOR)[1];
            const found = displayedData === null || displayedData === void 0 ? void 0 : displayedData.find(entry => String(entry.id) === String(entryId));
            return (React.createElement("div", { key: slot, slot: slot }, found && (expandRenderer === null || expandRenderer === void 0 ? void 0 : expandRenderer(found))));
        }
        if (type === SlotType.NoContent && noContentRenderer) {
            return (React.createElement("div", { key: slot, slot: slot }, noContentRenderer === null || noContentRenderer === void 0 ? void 0 : noContentRenderer()));
        }
    }), [columnProps, displayedData, expandRenderer, headerProps === null || headerProps === void 0 ? void 0 : headerProps.gridHeaderButtons, rowButtonProps, slots]);
    useEffect(() => {
        setSlotNodes(displaySlots());
    }, [slots, columnProps, rowButtonProps, headerProps === null || headerProps === void 0 ? void 0 : headerProps.gridHeaderButtons, expandRenderer]);
    return React.createElement(React.Fragment, null,
        loading && (React.createElement(Box, { sx: {
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            } },
            React.createElement(CircularProgress, null))),
        React.createElement("ap-data-grid", { "data-cy": apGridOtherProps.dataCy, "use-new-grid": true, language: language, theme: theme, ref: apGridRef }, slotNodes));
};
//# sourceMappingURL=ApDataGrid.js.map